// components/AI/AIImproveProposals.js — sub-tab "Proposals" di Review Findings.
//
// Proposal perbaikan yang ditulis agent review mingguan (backend/aiImprove.js).
// Approve → snapshot → eval baseline → terapkan → eval ulang → auto-rollback
// bila jumlah test lulus turun. Proposal kode/config hanya ditulis sebagai draft.
// Teks UI berbahasa Inggris, langsung di sini (bukan translations.js).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../context/AuthContext';
import {
  Loader2, RefreshCw, Bot, Check, X, RotateCcw, ChevronDown, ChevronUp, Pencil,
  AlertTriangle, CheckCircle2, FlaskConical, Lightbulb, BookOpen, Code2, Settings2, EyeOff, Brain,
} from 'lucide-react';

const TYPES = {
  memory_save:  { label: 'Team memory',          icon: Brain,        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  few_shot_add: { label: 'New few-shot example', icon: BookOpen,     cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  hint_append:  { label: 'Hint addition',        icon: Lightbulb,    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  test_case:    { label: 'Test case',            icon: FlaskConical, cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  dismiss:      { label: 'Dismiss findings',     icon: EyeOff,       cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  code:         { label: 'Code change (draft)',  icon: Code2,        cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  config_note:  { label: 'Config change (draft)', icon: Settings2,   cls: 'bg-violet-50 text-violet-700 border-violet-200' },
};
const STATUS_CLS = {
  pending: 'bg-slate-100 text-slate-600', applying: 'bg-blue-100 text-blue-700',
  applied: 'bg-emerald-100 text-emerald-700', rolled_back: 'bg-orange-100 text-orange-700',
  rejected: 'bg-slate-100 text-slate-400', failed: 'bg-red-100 text-red-700',
};
const parse = (s, d) => { try { return JSON.parse(s || ''); } catch (_) { return d; } };
function fmtTime(s) {
  if (!s) return '-';
  const str = String(s);
  const d = new Date(str.includes('T') ? str : str.replace(' ', 'T') + 'Z');
  return isNaN(d) ? str : d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function MiniTable({ rows }) {
  if (!Array.isArray(rows) || !rows.length) return null;
  const cols = Object.keys(rows[0]).slice(0, 8);
  return (
    <div className="overflow-x-auto mt-1 border border-slate-100 rounded-lg">
      <table className="text-[11px] min-w-max">
        <thead className="bg-slate-50"><tr>{cols.map(c => <th key={c} className="px-2 py-1 text-left font-semibold text-slate-500">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-100">{cols.map(c => <td key={c} className="px-2 py-1 text-slate-700">{String(r[c] ?? '')}</td>)}</tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{label}</div>
      {children}
    </div>
  );
}

function ProposalCard({ p, findings, onAction }) {
  const t = TYPES[p.type] || { label: p.type, icon: Bot, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  const Icon = t.icon;
  const payload = parse(p.payload, {});
  const v = parse(p.validation, {}) || {};
  const ids = parse(p.finding_ids, []) || [];
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(payload);
  const [showF, setShowF] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const pending = p.status === 'pending';
  const editable = pending && ['memory_save', 'few_shot_add', 'hint_append', 'test_case'].includes(p.type);

  const act = async (kind) => {
    setBusy(true); setErr('');
    try {
      const body = kind === 'approve' && edit ? { payload: draft } : {};
      await onAction(p.id, kind, body);
      setEdit(false);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  const ta = (key, rows, mono) => (
    <textarea value={draft[key] || ''} rows={rows} onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
      className={`w-full text-xs border border-slate-200 rounded-lg p-2 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400 ${mono ? 'font-mono' : ''}`} />
  );
  const pre = (s) => <pre className="text-[11px] font-mono bg-slate-50 rounded-lg p-2 whitespace-pre-wrap break-words text-slate-700">{s}</pre>;

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-md px-1.5 py-0.5 ${t.cls}`}>
          <Icon style={{ width: 11, height: 11 }} /> {t.label}
        </span>
        <span className={`text-[11px] font-semibold rounded-md px-1.5 py-0.5 ${STATUS_CLS[p.status] || ''}`}>
          {p.status === 'applying' && <Loader2 className="inline animate-spin mr-1" style={{ width: 10, height: 10 }} />}
          {p.status.replace('_', ' ')}
        </span>
        {p.source && <span className="text-[11px] font-mono text-slate-500 bg-slate-50 rounded-md px-1.5 py-0.5">{p.source}</span>}
        <span className="text-[11px] text-slate-400">{fmtTime(p.created_at)}</span>
        <span className="text-[11px] text-slate-300 ml-auto">#{p.id}</span>
      </div>
      <div className="text-sm font-semibold text-slate-800">{p.title}</div>
      {p.rationale && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{p.rationale}</div>}

      <div className="grid gap-2 mt-3">
        {(p.type === 'few_shot_add' || p.type === 'test_case') && (<>
          <Field label="Question">{edit ? ta('question', 2) : <div className="text-xs text-slate-700">{payload.question}</div>}</Field>
          <Field label={p.type === 'test_case' ? 'Expected SQL' : 'SQL'}>
            {edit ? ta(p.type === 'test_case' ? 'expected_sql' : 'sql', 6, true) : pre(payload.sql || payload.expected_sql || '')}
          </Field>
        </>)}
        {p.type === 'memory_save' && (<>
          <div className="text-[11px] text-slate-500">
            Scope: <b>{payload.scope === 'global' ? 'all dashboards' : `source ${p.source}`}</b> · {payload.kind === 'skill' ? 'procedure' : 'fact'} ·
            shared across all chats and all users
          </div>
          <Field label="Title">{edit ? ta('title', 1) : <div className="text-xs font-semibold text-slate-800">{payload.title}</div>}</Field>
          <Field label="Content">{edit ? ta('content', 5) : <div className="text-xs text-slate-700 bg-emerald-50/50 rounded-lg p-2 whitespace-pre-wrap">{payload.content}</div>}</Field>
        </>)}
        {p.type === 'hint_append' && (
          <Field label="Text to append to the hint">{edit ? ta('text', 4) : <div className="text-xs text-slate-700 bg-amber-50/50 rounded-lg p-2 whitespace-pre-wrap">{payload.text}</div>}</Field>
        )}
        {(p.type === 'code' || p.type === 'config_note') && (
          <Field label="Proposed change"><div className="text-xs text-slate-700 whitespace-pre-wrap">{payload.description || JSON.stringify(payload)}</div></Field>
        )}

        {pending && v && (v.ok === false ? (
          <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-2 flex gap-1.5">
            <AlertTriangle style={{ width: 13, height: 13 }} className="flex-shrink-0 mt-0.5" />
            <span>Validation failed: {v.error}. Edit the proposal before approving.</span>
          </div>
        ) : (<>
          {v.warning && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 flex gap-1.5">
              <AlertTriangle style={{ width: 13, height: 13 }} className="flex-shrink-0 mt-0.5" /><span>{v.warning}</span>
            </div>
          )}
          {v.row_count !== undefined && (
            <div className="text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle2 style={{ width: 13, height: 13 }} /> SQL runs and returns {v.row_count} row(s){v.preview?.length ? ' — preview:' : '.'}
            </div>
          )}
          <MiniTable rows={v.preview} />
        </>))}

        {p.result_note && (
          <div className={`text-xs rounded-lg p-2 ${p.status === 'applied' ? 'bg-emerald-50 text-emerald-800' : p.status === 'rolled_back' || p.status === 'failed' ? 'bg-orange-50 text-orange-800' : 'bg-slate-50 text-slate-600'}`}>
            {p.result_note}{p.decided_by ? ` — ${p.decided_by}, ${fmtTime(p.decided_at)}` : ''}
          </div>
        )}

        {ids.length > 0 && (
          <div>
            <button onClick={() => setShowF(s => !s)} className="text-[11px] text-brand-600 hover:underline flex items-center gap-0.5">
              {showF ? <ChevronUp style={{ width: 11, height: 11 }} /> : <ChevronDown style={{ width: 11, height: 11 }} />}
              {ids.length} related finding(s)
            </button>
            {showF && (
              <div className="mt-1 grid gap-1.5">
                {ids.map(id => {
                  const f = findings[id];
                  return (
                    <div key={id} className="text-[11px] bg-slate-50 rounded-lg p-2">
                      <span className="font-semibold text-slate-500">#{id} {f?.kind}</span>
                      {f ? <>
                        <div className="text-slate-700 mt-0.5"><b>Q:</b> {f.question}</div>
                        <div className="text-slate-500 mt-0.5"><b>A:</b> {f.answer}</div>
                        {f.correction && <div className="text-emerald-700 mt-0.5"><b>Correction:</b> {f.correction}</div>}
                      </> : <span className="text-slate-400"> (not found)</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
      {(pending || (p.status === 'applied' && p.snapshot)) && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
          {pending && (<>
            <button disabled={busy || (v.ok === false && !edit)} onClick={() => act('approve')}
              className="text-xs px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white flex items-center gap-1 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <Check style={{ width: 12, height: 12 }} />}
              {edit ? 'Save & approve' : 'Approve'}
            </button>
            {editable && (
              <button disabled={busy} onClick={() => { setEdit(e => !e); setDraft(payload); }}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1">
                <Pencil style={{ width: 12, height: 12 }} /> {edit ? 'Cancel edit' : 'Edit'}
              </button>
            )}
            <button disabled={busy} onClick={() => act('reject')}
              className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center gap-1">
              <X style={{ width: 12, height: 12 }} /> Reject
            </button>
            <span className="text-[11px] text-slate-400 ml-auto">
              {['memory_save', 'few_shot_add', 'hint_append'].includes(p.type) ? 'Runs regression tests before and after; rolls back if results get worse.'
                : p.type === 'test_case' ? 'Adds the case to the test set and runs it.'
                : p.type === 'dismiss' ? 'Marks the related findings as dismissed.'
                : 'Writes a draft to ai-drafts/proposals — nothing is changed automatically.'}
            </span>
          </>)}
          {p.status === 'applied' && p.snapshot && (
            <button disabled={busy} onClick={() => act('revert')}
              className="text-xs px-2.5 py-1.5 rounded-lg border border-orange-200 text-orange-700 hover:bg-orange-50 flex items-center gap-1">
              <RotateCcw style={{ width: 12, height: 12 }} /> Revert
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AIImproveProposals({ onChanged }) {
  const [filter, setFilter] = useState('pending');
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, s] = await Promise.all([
        api.get('/api/ai/improve/proposals', { params: { status: filter } }),
        api.get('/api/ai/improve/status'),
      ]);
      setData(p.data); setStatus(s.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  // Poll selama review/apply berjalan.
  const busyServer = status?.review_running || status?.applying || (data?.items || []).some(x => x.status === 'applying');
  useEffect(() => {
    clearTimeout(timer.current);
    if (busyServer) timer.current = setTimeout(() => { load(); onChanged && onChanged(); }, 5000);
    return () => clearTimeout(timer.current);
  }, [busyServer, data, load, onChanged]);

  const action = async (id, kind, body) => {
    await api.post(`/api/ai/improve/proposals/${id}/${kind}`, body || {});
    await load(); onChanged && onChanged();
  };
  const reviewNow = async () => {
    setError(null);
    try { await api.post('/api/ai/improve/review-now', { days }); await load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
  };

  const counts = {};
  (status?.proposals || []).forEach(r => { counts[r.status] = r.n; });
  const pendingN = counts.pending || 0;
  const doneN = Object.entries(counts).filter(([k]) => k !== 'pending' && k !== 'applying').reduce((a, [, n]) => a + n, 0);
  const lr = status?.last_review_result;
  const items = data?.items || [];

  return (
    <div>
      <div className="bg-white rounded-xl border border-slate-100 p-4 mb-4 flex items-center gap-3 flex-wrap">
        <Bot style={{ width: 18, height: 18 }} className="text-brand-600" />
        <div className="text-xs text-slate-600 flex-1 min-w-[220px]">
          <div className="font-semibold text-slate-800 text-sm">Weekly reviewer agent</div>
          {status?.review_running ? (
            <span className="text-blue-600 flex items-center gap-1"><Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> Reviewing findings…</span>
          ) : (
            <span>
              Last run: {status?.last_review_at ? fmtTime(status.last_review_at) : 'never'}
              {lr && !lr.error && lr.proposals !== undefined && ` · ${lr.proposals} proposal(s) from ${lr.findings} finding(s)`}
              {lr?.error && <span className="text-red-600"> · Error: {lr.error}</span>}
              {lr && lr.auto_applied !== undefined && ` · auto-applied ${lr.auto_applied}${lr.auto_rolled_back ? `, ${lr.auto_rolled_back} rolled back` : ''}`}
              {' · '}Runs every Monday 07:23 · Model: {status?.reviewer || '-'}
            </span>
          )}
        </div>
        <button onClick={async () => { await api.post('/api/ai/improve/auto-approve', { enabled: !status?.auto_approve }); load(); }}
          title="Auto-approve applies test cases, dismissals and dashboard-scoped team memory right after each review. A single confirmed test regression rolls the memory back. Hints, few-shot, global memory, code and config always wait for you."
          className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1 ${status?.auto_approve ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'}`}>
          <CheckCircle2 style={{ width: 13, height: 13 }} /> Auto-approve: {status?.auto_approve ? 'On' : 'Off'}
        </button>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
          <option value={7}>Last 7 days</option><option value={14}>Last 14 days</option><option value={30}>Last 30 days</option>
        </select>
        <button onClick={reviewNow} disabled={!!status?.review_running}
          className="text-xs px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white flex items-center gap-1 disabled:opacity-50">
          <Bot style={{ width: 13, height: 13 }} /> Run review now
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {[['pending', `Pending ${pendingN}`], ['done', `Decided ${doneN}`], ['all', 'All']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === k ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
        ))}
        <button onClick={load} disabled={loading}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50">
          <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3 mb-3">{error}</div>}
      {data && !items.length && (
        <div className="text-sm text-slate-400 bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center">
          No proposals here. Run the reviewer to generate proposals from open findings.
        </div>
      )}
      <div className="grid gap-3">
        {items.map(p => <ProposalCard key={p.id + p.status} p={p} findings={data.findings || {}} onAction={action} />)}
      </div>
    </div>
  );
}
